import type { FC, ReactElement } from "react";
import { useCallback, useEffect, useState } from "react";

import { SelectedAddressView, SelectedAddressViewCard, useFeatureFlags } from "@jti/commerce";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { Button } from "@jti/core-components/components/Button";
import { Loader } from "@jti/core-components/components/Loader";
import { Dialog, useTranslations } from "@jti/ui";

import { WIDGET_EVENT_CLOSE_MODAL } from "../../CustomShippingOptionWidget.events";
import type { CustomShippingOptionWidgetVariantsCommonProps } from "../../CustomShippingOptionWidget.types";
import { prepareAddressViewFormat } from "../../utils/prepareAddressViewFormat";

import styles from "./CustomShippingOptionModal.module.scss";

export const CustomShippingOptionWidgetVariantModal: FC<CustomShippingOptionWidgetVariantsCommonProps> = ({
  webComponentRef,
  scriptStatus,
  selectedAddress,
  widgetError,
  children,
}): ReactElement => {
  const { __ } = useTranslations();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { ffCheckoutV2EnableIteration2 } = useFeatureFlags();

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const setupEventListeners = () => {
    if (!webComponentRef.current) {
      return;
    }

    webComponentRef.current.addEventListener(WIDGET_EVENT_CLOSE_MODAL, closeModal);

    return () => {
      if (!webComponentRef.current) {
        return;
      }

      webComponentRef.current.removeEventListener(WIDGET_EVENT_CLOSE_MODAL, closeModal);
    };
  };

  const toggleModal = useCallback(() => {
    setIsModalOpen((prevState) => !prevState);
  }, []);

  useEffect(() => {
    const cleanup = setupEventListeners();

    return cleanup;
    // TODO - fix deps arrays values after exhaustive-deps as error - DESP-111178
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {selectedAddress && ffCheckoutV2EnableIteration2 && (
        <SelectedAddressViewCard
          title={__("cart.checkout.delivery.custom.title")}
          changeBtnText={__("cart.checkout.delivery.custom.change_cta")}
          onChange={toggleModal}
        >
          <SelectedAddressView title={selectedAddress.storeName} address={prepareAddressViewFormat(selectedAddress)} />
        </SelectedAddressViewCard>
      )}

      {selectedAddress && !ffCheckoutV2EnableIteration2 && (
        <SelectedAddressView title={selectedAddress.storeName} address={prepareAddressViewFormat(selectedAddress)}>
          <Button onClick={toggleModal} variant="text" data-testid="custom-delivery-change-store-cta">
            {__("cart.checkout.delivery.custom.change_address")}
          </Button>
        </SelectedAddressView>
      )}

      {!selectedAddress && (
        <Button onClick={toggleModal} variant="primary" data-testid="custom-delivery-select-store-cta">
          {__("cart.checkout.delivery.custom.cta")}
        </Button>
      )}

      <Dialog isOpened={isModalOpen} onClose={toggleModal} className={styles.dialogContainer} fullscreen closable>
        {scriptStatus === "loading" && (
          <div className={styles.loadingWrapper}>
            <Loader data-testid="custom-delivery-modal-loading" />
          </div>
        )}

        {scriptStatus === "error" && (
          <AlertBanner
            tone="error"
            data-testid="widget-error"
            description={__("cart.checkout.delivery.custom.error.script_loading")}
          />
        )}

        {widgetError?.code === "WIDGET_INITIALIZATION_FAIL" && (
          <AlertBanner
            tone="error"
            data-testid="widget-error"
            description={__("cart.checkout.delivery.custom.error.widget_initializing")}
          />
        )}

        {children}
      </Dialog>
    </>
  );
};
